import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Compass, Users, Plus, MessageCircle, User } from 'lucide-react';
import { cn } from '@/lib/utils';

const navItems = [
  { path: '/feed', icon: Compass, label: 'Feed' },
  { path: '/friends', icon: Users, label: 'Amis' },
  { path: '/create', icon: Plus, label: 'Créer', isMain: true },
  { path: '/messages', icon: MessageCircle, label: 'Messages' },
  { path: '/profile', icon: User, label: 'Profil' },
];

export function BottomNavigation() {
  const location = useLocation();

  return (
    <motion.nav 
      className="fixed bottom-0 left-0 right-0 z-50 pb-safe"
      initial={{ y: 100 }}
      animate={{ y: 0 }}
      transition={{ 
        duration: 0.4, 
        ease: [0.25, 0.1, 0.25, 1] as [number, number, number, number],
      }}
    >
      {/* Themed glass background */}
      <div className="absolute inset-0 bg-background/95 backdrop-blur-2xl border-t border-border" />
      
      {/* Subtle top gradient */}
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
      
      <div className="relative flex items-center justify-around h-[72px] max-w-lg mx-auto px-3">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path || 
            (item.path === '/feed' && location.pathname === '/');
          const Icon = item.icon;
          
          if (item.isMain) {
            return (
              <Link
                key={item.path}
                to={item.path}
                className="relative -top-4"
                aria-label={item.label}
              >
                <motion.div 
                  className="relative"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.92 }}
                  transition={{ type: "spring", stiffness: 400, damping: 17 }}
                >
                  {/* Outer glow ring */}
                  <motion.div 
                    className="absolute inset-[-3px] rounded-full"
                    style={{
                      background: 'linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary)))',
                      opacity: 0.5,
                      filter: 'blur(10px)',
                    }}
                    animate={{ 
                      scale: [1, 1.2, 1],
                      opacity: [0.4, 0.6, 0.4],
                    }}
                    transition={{ 
                      duration: 2,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  />
                  
                  {/* Main button */}
                  <div className={cn(
                    "relative flex items-center justify-center w-14 h-14 rounded-full",
                    "bg-gradient-to-br from-primary to-primary/80",
                    "shadow-2xl"
                  )}
                  style={{
                    boxShadow: '0 8px 32px hsl(var(--primary) / 0.5), inset 0 1px 0 hsl(0 0% 100% / 0.2)',
                  }}
                  >
                    <Icon className="h-7 w-7 text-primary-foreground" strokeWidth={2.5} aria-hidden="true" />
                    <span className="sr-only">{item.label}</span>
                  </div>
                </motion.div>
              </Link>
            );
          }
          
          return (
            <Link
              key={item.path}
              to={item.path}
              className="flex flex-col items-center justify-center gap-1 py-2 px-4 relative"
              aria-label={item.label}
            >
              <motion.div
                className="relative flex flex-col items-center"
                whileTap={{ scale: 0.92 }}
                transition={{ duration: 0.1 }}
              >
                {/* Active background pill */}
                <AnimatePresence>
                  {isActive && (
                    <motion.div 
                      className="absolute -inset-x-2 -inset-y-1 rounded-xl bg-primary/15"
                      layoutId="activeNavTab"
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      transition={{ type: "spring", stiffness: 400, damping: 30 }}
                    />
                  )}
                </AnimatePresence>
                
                <div className="relative">
                  <Icon 
                    className={cn(
                      "h-6 w-6 relative z-10 transition-all duration-200",
                      isActive ? "text-primary" : "text-muted-foreground"
                    )} 
                    strokeWidth={isActive ? 2.5 : 1.8} 
                  />
                  
                  {/* Active dot indicator */}
                  <AnimatePresence>
                    {isActive && (
                      <motion.div 
                        className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-primary"
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0 }}
                        transition={{ duration: 0.2 }}
                      />
                    )}
                  </AnimatePresence>
                </div>
                
                <motion.span 
                  className={cn(
                    "text-[10px] font-medium mt-1.5 transition-all duration-200",
                    isActive ? "text-primary" : "text-muted-foreground"
                  )}
                  animate={{ 
                    opacity: isActive ? 1 : 0.8,
                  }}
                >
                  {item.label}
                </motion.span>
              </motion.div>
            </Link>
          );
        })}
      </div>
    </motion.nav>
  );
}
